import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {AuthStateService} from '@auth/states';
import {CompanyStateService} from '@core/states';
import {ApiCollection, ApiResponse, Membership, TaskAssignee} from '@core/types';
import {ENDPOINTS, GET_URL, log} from '@core/utils';
import {HttpService} from '@core/services';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(
    private apiService: HttpService,
    private companyState: CompanyStateService,
    private authState: AuthStateService
  ) {}

  async getUsers(page = 1, itemsPerPage = 100, options?: {skipLoader?: boolean}): Promise<ApiResponse<TaskAssignee[]>> {
    try {
      const url = GET_URL(ENDPOINTS.USERS, {page, itemsPerPage});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const users = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapUser(item));
      return {success: true, result: users};
    } catch (error: any) {
      log.error('Error loading users:', error);
    }
    return {success: false, message: 'No se pudieron cargar los usuarios.'};
  }

  async getCurrentCompanyUsers(options?: {skipLoader?: boolean}): Promise<ApiResponse<TaskAssignee[]>> {
    try {
      const url = GET_URL(ENDPOINTS.COMPANY_MEMBERSHIPS, {pagination: 0});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<Membership> = await this.apiService.get(url, headers ? {headers} : undefined);
      const memberships = this.apiService.normalizeCollection<Membership>(response).member;
      const users = memberships
        .filter(item => !!item.userId)
        .map(item => ({
          id: item.userId as string | number,
          name: item.userName || item.employeeName || item.userEmail || 'Sin nombre',
          email: item.userEmail || undefined
        }))
        .filter((item, index, list) => list.findIndex(candidate => candidate.id === item.id) === index);
      return {success: true, result: users};
    } catch (error: any) {
      log.error('Error loading current company users:', error);
    }
    return {success: false, message: 'No se pudieron cargar los responsables de la empresa.'};
  }

  private mapUser(user: any): TaskAssignee {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    const name = user.name || `${firstName} ${lastName}`.trim() || 'Sin nombre';
    return {
      id: user.id,
      name,
      email: user.email
    };
  }

  private buildHeaders(skipLoader = false): HttpHeaders | null {
    if (this.authState.isAdmin()) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    let headers = new HttpHeaders().set('X-Selected-Company-ID', String(companyId));
    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', '1');
    }
    return headers;
  }
}
